const excludedImageSelector = [
  '[data-disable-image-loading]',
  '.placeholder-svg',
  '.payment-icon',
  '.shopify-payment-button img',
  'header img[class*="logo"]',
  'img[class*="icon"]',
].join(',');

const loadingImages = new WeakSet();
const animatedImages = new WeakSet();

// Native lazy loading keeps images deferred; visible content gets network priority.
const priorityObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver(entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const image = entry.target;
        if (image.closest('[aria-hidden="true"], .piv2__slide:not(.is-active), [data-hero-v2-slide]:not(.is-active)')) continue;
        image.fetchPriority = 'high';
        image.loading = 'eager';
        priorityObserver.unobserve(image);
      }
    }, { rootMargin: '0px' })
  : null;

const visibilityObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-image-loading-visible');
        visibilityObserver.unobserve(entry.target);
      });
    }, { rootMargin: '200px 0px' })
  : null;

const finishImageLoading = image => {
  if (!loadingImages.has(image)) return;
  loadingImages.delete(image);
  priorityObserver?.unobserve(image);
  visibilityObserver?.unobserve(image);
  image.classList.remove('global-image-loading', 'is-image-loading-visible');

  if (!image.complete || !image.naturalWidth || animatedImages.has(image)) return;
  animatedImages.add(image);
  image.classList.add('global-image-loaded');
  image.addEventListener('animationend', () => image.classList.remove('global-image-loaded'), { once: true });
  window.setTimeout(() => image.classList.remove('global-image-loaded'), 300);
};

const prepareImage = image => {
  if (!(image instanceof HTMLImageElement) || loadingImages.has(image) || image.matches(excludedImageSelector)) return;
  if (image.loading === 'lazy' && !image.complete) {
    image.fetchPriority = 'low';
    priorityObserver?.observe(image);
  }
  if (image.complete) return;

  loadingImages.add(image);
  image.classList.add('global-image-loading');
  const finish = () => {
    image.removeEventListener('load', finish);
    image.removeEventListener('error', finish);
    finishImageLoading(image);
  };
  image.addEventListener('load', finish);
  image.addEventListener('error', finish);

  if (visibilityObserver) visibilityObserver.observe(image);
  else image.classList.add('is-image-loading-visible');

  if (image.complete) finishImageLoading(image);
};

const prepareImagesWithin = node => {
  if (!(node instanceof Element)) return;
  if (node instanceof HTMLImageElement) prepareImage(node);
  node.querySelectorAll('img').forEach(prepareImage);
};

document.querySelectorAll('img').forEach(prepareImage);

new MutationObserver(records => {
  records.forEach(record => {
    if (record.type === 'attributes') {
      animatedImages.delete(record.target);
      record.target.classList.remove('global-image-loaded');
      prepareImage(record.target);
      return;
    }
    record.addedNodes.forEach(prepareImagesWithin);
  });
}).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['src', 'srcset'],
  childList: true,
  subtree: true,
});
